import type { ReactNode } from "react";
import { formatApiDateTime, apiString } from "./payoutApiAdapters";
import { rupeesToPaise } from "./payoutMutationHelpers";

type ApiRecord = Record<string, unknown>;

export type ApprovalRuleStageGroupIds = {
  M?: string;
  C?: string;
  A?: string;
  U?: string;
};

export type ApprovalRuleWire = {
  name: string;
  appliesTo: string;
  type: string;
  condition: string;
  workflow: string[];
  priority: string;
  status: string;
  icon?: ReactNode;
  tone?: string;
  workflowId?: string;
  approvalGroupId?: string;
  approvalGroupName?: string;
  /** Distinct group id per approval stage (preferred over single approvalGroupId). */
  stageGroupIds?: ApprovalRuleStageGroupIds;
  amountMinPaise?: number;
  amountMaxPaise?: number | null;
  ruleId?: string;
  createdAt?: string;
  createdBy?: string;
  lastUpdated?: string;
  description?: string;
};

export type ApprovalGroupLevelWire = {
  stage: "M" | "C" | "A" | "U";
  title: string;
  users: string[];
  mode: "Create Only" | "Any One Approves" | "All Must Approve" | "Minimum N Approvals";
  minApprovals?: number;
};

export type ApprovalGroupWire = {
  id: string;
  name: string;
  description: string;
  levels: string;
  flow: string;
  users: string[];
  levelDetails?: ApprovalGroupLevelWire[];
  createdAt: string;
  createdBy: string;
  lastUpdated: string;
  status: "Active" | "Inactive";
  tone: string;
  memberUserIds?: string[];
  apiStage?: "MAKER" | "CHECKER" | "APPROVER" | "AUDITOR";
  apiLogic?: "ANY_ONE" | "ALL_MUST" | "ALL" | "MIN_N";
};

export type RuleMatrixRowWire = {
  amount: string;
  condition: string;
  workflow: string[];
  priority: string;
  status: string;
  workflowId?: string;
  amountMinPaise?: number;
  amountMaxPaise?: number | null;
};

const formatMatrixRupees = (paise: number | null | undefined) => {
  const rupees = Math.max(0, Math.round(Number(paise ?? 0) / 100));
  return rupees.toLocaleString("en-IN");
};

const matrixRangeFromBounds = (
  minPaise: number | undefined,
  maxPaise: number | null | undefined,
) => {
  if (typeof maxPaise === "number") {
    if (!minPaise || minPaise <= 1) {
      return `\u2264 \u20B9${formatMatrixRupees(maxPaise)}`;
    }
    return `\u20B9${formatMatrixRupees(minPaise)} - \u20B9${formatMatrixRupees(maxPaise)}`;
  }
  if (typeof minPaise === "number" && minPaise > 1) {
    return `> \u20B9${formatMatrixRupees(minPaise - 1)}`;
  }
  return "";
};

const matrixRangeBoundsFromText = (amount: string) => {
  const text = amount.trim();
  const values = (text.match(/[\d,]+/g) || []).map((value) =>
    rupeesToPaise(value.replace(/,/g, "")),
  );

  if (text.includes("-")) {
    return {
      amountMinPaise: values[0] || 1,
      amountMaxPaise: values[1] || null,
    };
  }
  if (text.includes(">")) {
    return {
      amountMinPaise: (values[0] || 0) + 1,
      amountMaxPaise: null,
    };
  }
  return {
    amountMinPaise: 1,
    amountMaxPaise: values[0] || null,
  };
};

const PRIORITY_LABELS = ["Urgent", "High", "Medium", "Low"] as const;

export const normalizeApprovalPriority = (priority: unknown) => {
  if (priority === null || priority === undefined || String(priority).trim() === "") return "";
  if (priority === 1 || priority === "1" || priority === "1 (Highest)") return "Urgent";
  if (priority === 2 || priority === "2") return "High";
  if (priority === 3 || priority === "3") return "Medium";
  if (priority === 4 || priority === "4" || priority === 5 || priority === "5") return "Low";
  const text = String(priority || "").trim();
  if (PRIORITY_LABELS.includes(text as (typeof PRIORITY_LABELS)[number])) return text;
  return "";
};

const APPLIES_TO_LABEL: Record<string, string> = {
  ALL: "All Transactions",
  PAYOUTS: "Payouts",
  TRANSFERS: "Transfers",
  RECHARGES: "Recharges",
};

const APPLIES_TO_WIRE: Record<string, string> = {
  "All Transactions": "ALL",
  Payouts: "PAYOUTS",
  Transfers: "TRANSFERS",
  Recharges: "RECHARGES",
};

export const appliesToFromWire = (value: string) =>
  APPLIES_TO_LABEL[value.trim().toUpperCase()] || value || "";

export const appliesToToWire = (value: string) =>
  APPLIES_TO_WIRE[value] || "ALL";

export const getRuleRouteKey = (rule: Pick<ApprovalRuleWire, "workflowId" | "name">) =>
  rule.workflowId || rule.name;

export const findRuleByRouteKey = (rules: ApprovalRuleWire[], routeKey: string) =>
  rules.find(
    (rule) => rule.workflowId === routeKey || rule.name === routeKey,
  );

export const getRuleEditPath = (rule: Pick<ApprovalRuleWire, "workflowId" | "name">) =>
  `/payouts/approval-rules/workflow-rules/${encodeURIComponent(getRuleRouteKey(rule))}/edit`;

export const getRuleDetailsPath = (rule: Pick<ApprovalRuleWire, "workflowId" | "name">) =>
  `/payouts/approval-rules/workflow-rules/${encodeURIComponent(getRuleRouteKey(rule))}`;

const approvalApiStatus = (
  status: unknown,
  fallback: "Active" | "Inactive" = "Active",
): "Active" | "Inactive" => {
  const normalized = String(status || fallback).trim().toLowerCase();
  return ["inactive", "disabled", "deleted"].includes(normalized) ? "Inactive" : "Active";
};

const approvalApiStrings = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string" || typeof item === "number") return String(item);
        if (item && typeof item === "object") {
          return apiString(item as ApiRecord, [
            "user_id",
            "userId",
            "initials",
            "stage",
            "role",
            "name",
            "display_name",
            "displayName",
            "full_name",
            "fullName",
            "label",
            "email",
          ]);
        }
        return "";
      })
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const workflowStagesFromApi = (row: ApiRecord, fallback: string[] = []) => {
  const uiStages = row.ui_stages || row.uiStages;
  if (Array.isArray(uiStages)) {
    return uiStages.map(String).filter((stage) => ["M", "C", "A", "U"].includes(stage));
  }
  const raw =
    row.workflow ||
    row.workflow_path ||
    row.workflowPath ||
    row.stages ||
    row.stage_path ||
    row.stagePath;
  const stages = approvalApiStrings(raw).map((stage) => {
    const normalized = stage.trim().toUpperCase();
    if (normalized.startsWith("MAKER")) return "M";
    if (normalized.startsWith("CHECKER")) return "C";
    if (normalized.startsWith("APPROVER")) return "A";
    if (normalized.startsWith("AUDITOR")) return "U";
    return normalized.slice(0, 1);
  });
  const safeStages = stages.filter((stage) => ["M", "C", "A", "U"].includes(stage));
  return safeStages.length ? safeStages : fallback;
};

const workflowStageRecordsFromApi = (row: ApiRecord): ApiRecord[] => {
  const raw = row.stages || row.workflow_stages || row.workflowStages;
  return Array.isArray(raw)
    ? raw.filter((item): item is ApiRecord => Boolean(item) && typeof item === "object")
    : [];
};

const stageTitleFromCode = (stage: string) => {
  if (stage === "C") return "Checker";
  if (stage === "A") return "Approver";
  if (stage === "U") return "Auditor";
  return "Maker";
};

const logicLabelFromWire = (logic?: string) => {
  if (logic === "ALL_MUST" || logic === "ALL") return "All Must Approve" as const;
  if (logic === "MIN_N") return "Minimum N Approvals" as const;
  return "Any One Approves" as const;
};

const logicToWire = (mode?: string): "ANY_ONE" | "ALL_MUST" => {
  if (mode === "All Must Approve" || mode === "Minimum N Approvals") return "ALL_MUST";
  return "ANY_ONE";
};

const stageToWire = (stage?: string): "MAKER" | "CHECKER" | "APPROVER" | "AUDITOR" => {
  if (stage === "M") return "MAKER";
  if (stage === "A") return "APPROVER";
  if (stage === "U") return "AUDITOR";
  return "CHECKER";
};

const stageFromLevelDetails = (levels?: ApprovalGroupLevelWire[]) => {
  const primary = levels?.[0];
  return stageToWire(primary?.stage);
};

const approvalGroupLevelDetailsFromApi = (row: ApiRecord): ApprovalGroupLevelWire[] => {
  const raw =
    row.ui_level_details ||
    row.uiLevelDetails ||
    row.level_details ||
    row.levelDetails ||
    row.approval_levels ||
    row.approvalLevels;
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item): item is ApiRecord => Boolean(item) && typeof item === "object")
    .map((item) => {
      const stage = apiString(item, ["stage", "ui_stage", "uiStage"], "C");
      const normalizedStage = ["M", "C", "A", "U"].includes(stage) ? stage : stage.slice(0, 1);
      const safeStage = ["M", "C", "A", "U"].includes(normalizedStage)
        ? normalizedStage
        : "C";
      return {
        stage: safeStage as ApprovalGroupLevelWire["stage"],
        title: apiString(item, ["title", "name", "label"], stageTitleFromCode(safeStage)),
        users: approvalApiStrings(item.users || item.member_user_ids || item.memberUserIds),
        mode: apiString(
          item,
          ["mode", "approval_mode", "approvalMode", "logic_label", "logicLabel"],
          logicLabelFromWire(apiString(item, ["logic"], "")),
        ) as ApprovalGroupLevelWire["mode"],
        minApprovals:
          typeof item.min_approvals === "number"
            ? item.min_approvals
            : typeof item.minApprovals === "number"
              ? item.minApprovals
              : undefined,
      };
    });
};

export const approvalRuleFromApiRecord = (
  row: ApiRecord,
  index = 0,
  presentation?: { icon?: ReactNode; tone?: string },
): ApprovalRuleWire => {
  const amountMinPaise = typeof row.amount_min_paise === "number" ? row.amount_min_paise : undefined;
  const amountMaxPaise =
    row.amount_max_paise === null
      ? null
      : typeof row.amount_max_paise === "number"
        ? row.amount_max_paise
        : undefined;
  const stageRecords = workflowStageRecordsFromApi(row);
  const stageGroupIds: ApprovalRuleStageGroupIds = {};
  for (const stageRecord of stageRecords) {
    const stageName = apiString(stageRecord, ["stage"], "").toUpperCase();
    const groupId = apiString(stageRecord, ["group_id", "groupId"], "");
    if (!groupId) continue;
    if (stageName === "MAKER" || stageName === "M") stageGroupIds.M = groupId;
    if (stageName === "CHECKER" || stageName === "C") stageGroupIds.C = groupId;
    if (stageName === "APPROVER" || stageName === "A") stageGroupIds.A = groupId;
    if (stageName === "AUDITOR" || stageName === "U") stageGroupIds.U = groupId;
  }
  const approvalGroupId =
    stageGroupIds.M ||
    stageGroupIds.C ||
    stageGroupIds.A ||
    stageGroupIds.U ||
    apiString(
      stageRecords.find((stage) => apiString(stage, ["group_id", "groupId"], "")) || row,
      ["group_id", "groupId", "approval_group_id", "approvalGroupId"],
    );

  return {
    workflowId: apiString(row, ["workflow_id", "workflowId", "id"]),
    ruleId: apiString(row, ["rule_id", "ruleId", "workflow_id", "workflowId", "id"]),
    name: apiString(row, ["name", "rule_name", "ruleName", "workflow_name", "workflowName"], ""),
    appliesTo: appliesToFromWire(
      apiString(row, ["applies_to", "appliesTo", "transaction_type", "transactionType"], ""),
    ),
    type: apiString(row, ["type", "rule_type", "ruleType", "workflow_type", "workflowType"], ""),
    condition: apiString(
      row,
      ["amount_label", "amountLabel", "condition", "description"],
      "",
    ),
    workflow: workflowStagesFromApi(row),
    priority: normalizeApprovalPriority(
      row.ui_priority || row.uiPriority || row.priority,
    ),
    status: approvalApiStatus(row.status || row.ui_status || row.uiStatus, "Active"),
    approvalGroupId,
    approvalGroupName: apiString(row, ["approval_group_name", "approvalGroupName", "group_name", "groupName"], ""),
    stageGroupIds: Object.keys(stageGroupIds).length ? stageGroupIds : undefined,
    amountMinPaise,
    amountMaxPaise,
    createdAt: formatApiDateTime(apiString(row, ["created_at", "createdAt"], "")),
    createdBy: apiString(row, ["created_by", "createdBy", "creator_name", "creatorName"], ""),
    lastUpdated: formatApiDateTime(
      apiString(row, ["updated_at", "updatedAt", "last_updated", "lastUpdated"], ""),
    ),
    description: apiString(row, ["description", "remarks", "note"], ""),
    icon: presentation?.icon,
    tone: presentation?.tone,
  };
};

export const approvalGroupFromApiRecord = (
  row: ApiRecord,
  index = 0,
  presentation?: { tone?: string },
): ApprovalGroupWire => {
  const memberUserIds = approvalApiStrings(row.member_user_ids || row.memberUserIds);
  const stageCode = apiString(row, ["ui_stage", "uiStage"], "");
  const stage = apiString(row, ["stage"], stageCode === "A" ? "APPROVER" : stageCode === "U" ? "AUDITOR" : stageCode === "C" ? "CHECKER" : "") as
    | "CHECKER"
    | "APPROVER"
    | "AUDITOR";
  const logic = apiString(row, ["logic"], "") as "ANY_ONE" | "ALL_MUST" | "ALL" | "MIN_N";
  const uiStage = (stageCode || (stage === "APPROVER" ? "A" : stage === "AUDITOR" ? "U" : stage === "CHECKER" ? "C" : "")) as
    | "M"
    | "C"
    | "A"
    | "U";
  // Backend stores one stage per group — prefer API stage + members over legacy multi-level UI payload.
  const uiLevelDetails = approvalGroupLevelDetailsFromApi(row);
  const fallbackLevelDetails: ApprovalGroupLevelWire[] = uiStage
    ? [
        {
          stage: uiStage,
          title: stageTitleFromCode(uiStage),
          users: memberUserIds,
          mode: logic ? logicLabelFromWire(logic) : "Any One Approves",
        },
      ]
    : [];
  const levelDetails =
    uiLevelDetails.length > 0 ? uiLevelDetails : fallbackLevelDetails;

  return {
    id: apiString(row, ["group_id", "groupId", "id"], `group-${index + 1}`),
    name: apiString(row, ["name", "group_name", "groupName"], ""),
    description: apiString(row, ["description"], ""),
    levels: apiString(
      row,
      ["ui_levels", "uiLevels", "levels"],
      levelDetails.length ? `${levelDetails.length} ${levelDetails.length === 1 ? "Level" : "Levels"}` : "",
    ),
    flow: apiString(row, ["ui_flow", "uiFlow", "flow"], levelDetails.map((level) => level.title).join(" -> ")),
    users: memberUserIds,
    memberUserIds,
    apiStage: stage,
    apiLogic: logic === "ALL" ? "ALL_MUST" : logic,
    levelDetails,
    createdAt: formatApiDateTime(
      apiString(row, ["created_at", "createdAt"], ""),
      "-",
    ),
    createdBy: "-",
    lastUpdated: formatApiDateTime(
      apiString(row, ["updated_at", "updatedAt", "last_updated", "lastUpdated"], ""),
      "-",
    ),
    status: approvalApiStatus(row.ui_status || row.status, "Active"),
    tone: presentation?.tone || "bg-[#6687FF1A] text-[#6687FF]",
  };
};

export const ruleMatrixRowsFromApiRecord = (record: ApiRecord): RuleMatrixRowWire[] => {
  const rows = Array.isArray(record.rows)
    ? record.rows.filter((row): row is ApiRecord => Boolean(row) && typeof row === "object")
    : [];
  if (!rows.length) return [];

  return rows.map((row) => {
    const amountMinPaise =
      typeof row.amount_min_paise === "number" ? row.amount_min_paise : undefined;
    const amountMaxPaise =
      row.amount_max_paise === null
        ? null
        : typeof row.amount_max_paise === "number"
          ? row.amount_max_paise
          : undefined;
    const amount =
      apiString(row, ["amount", "amount_range", "amountRange", "range"], "") ||
      matrixRangeFromBounds(amountMinPaise, amountMaxPaise);

    return {
      workflowId: apiString(row, ["workflow_id", "workflowId"]),
      amount,
      condition: apiString(row, ["condition", "description", "name"], ""),
      workflow: workflowStagesFromApi(row),
      priority: normalizeApprovalPriority(row.priority || row.ui_priority || row.uiPriority),
      status: approvalApiStatus(row.status, "Active"),
      amountMinPaise,
      amountMaxPaise,
    };
  });
};

const parseConditionAmounts = (rule: ApprovalRuleWire) => {
  if (rule.amountMinPaise !== undefined) {
    return {
      amountMinPaise: rule.amountMinPaise,
      amountMaxPaise: rule.amountMaxPaise ?? null,
    };
  }

  const amounts = rule.condition.match(/[\d,]+/g) || [];
  const lastAmount = amounts[amounts.length - 1]?.replace(/,/g, "") || "0";
  const paise = rupeesToPaise(Number(lastAmount));
  if (rule.condition.includes("<=") || rule.condition.includes("≤")) {
    return { amountMinPaise: 1, amountMaxPaise: paise };
  }
  if (rule.condition.includes(">=")) {
    return { amountMinPaise: paise, amountMaxPaise: null };
  }
  if (rule.condition.includes(">")) {
    return { amountMinPaise: paise + 1, amountMaxPaise: null };
  }
  return { amountMinPaise: 1, amountMaxPaise: paise || null };
};

export const approvalRulePayload = (rule: ApprovalRuleWire): Record<string, unknown> => {
  const { amountMinPaise, amountMaxPaise } = parseConditionAmounts(rule);
  const stages = rule.workflow.length ? rule.workflow : ["M"];
  const stageGroupIds = rule.stageGroupIds || {};
  const approvalStages = stages
    .map((stage) => {
      const groupId =
        stage === "M"
          ? stageGroupIds.M
          : stage === "A"
            ? stageGroupIds.A
            : stage === "U"
              ? stageGroupIds.U
              : stageGroupIds.C || rule.approvalGroupId;
      if (!groupId) return null;
      return {
        stage:
          stage === "M"
            ? "MAKER"
            : stage === "A"
              ? "APPROVER"
              : stage === "U"
                ? "AUDITOR"
                : "CHECKER",
        group_id: groupId,
        logic: "ANY_ONE",
      };
    })
    .filter((stage): stage is { stage: string; group_id: string; logic: string } => Boolean(stage));
  return {
    name: rule.name,
    description: rule.condition,
    applies_to: appliesToToWire(rule.appliesTo),
    amount_min_paise: amountMinPaise,
    amount_max_paise: amountMaxPaise,
    ...(approvalStages.length ? { stages: approvalStages } : { ui_stages: stages }),
    ui_priority: rule.priority,
    active: rule.status !== "Inactive",
  };
};

export const approvalGroupPayload = (group: ApprovalGroupWire): Record<string, unknown> => {
  const levels = group.levelDetails?.length ? group.levelDetails : [];
  const primaryLevel = levels.find((level) => level.stage !== "M") || levels[0];
  const stage =
    group.apiStage ||
    (primaryLevel ? stageToWire(primaryLevel.stage) : stageFromLevelDetails(levels));
  const rawLogic =
    group.apiLogic ||
    logicToWire(primaryLevel?.mode || levels[0]?.mode);
  const logic = rawLogic === "ALL" || rawLogic === "MIN_N" ? "ALL_MUST" : rawLogic;
  const memberUserIds =
    group.memberUserIds?.length
      ? group.memberUserIds
      : levels.flatMap((level) => level.users).filter((user) => user.startsWith("usr_")) || [];

  const uiLevelDetails = levels.map((level) => ({
    stage: level.stage,
    title: level.title,
    users: level.users.filter((user) => user.startsWith("usr_")),
    mode: level.mode,
    ...(level.minApprovals ? { min_approvals: level.minApprovals } : {}),
    logic: logicToWire(level.mode),
  }));

  return {
    name: group.name,
    description: group.description,
    stage,
    member_user_ids: memberUserIds,
    logic,
    ui_levels: "1 Level",
    ui_flow: stage === "APPROVER" ? "Approver" : stage === "AUDITOR" ? "Auditor" : "Checker",
    ui_level_details: [
      {
        stage: stage === "APPROVER" ? "A" : stage === "AUDITOR" ? "U" : "C",
        title: stage === "APPROVER" ? "Approver" : stage === "AUDITOR" ? "Auditor" : "Checker",
        users: memberUserIds,
        mode: logicLabelFromWire(logic),
      },
    ],
    status: group.status === "Inactive" ? "INACTIVE" : "ACTIVE",
  };
};

export const ruleMatrixPayload = (rows: RuleMatrixRowWire[]): Record<string, unknown> => ({
  rows: rows.map((row, index) => {
    const bounds = matrixRangeBoundsFromText(row.amount);
    return {
      workflow_id: row.workflowId,
      name: row.condition || row.amount || `Matrix band ${index + 1}`,
      condition: row.condition,
      amount: row.amount,
      amount_range: row.amount,
      amount_min_paise: bounds.amountMinPaise,
      amount_max_paise: bounds.amountMaxPaise,
      ui_stages: row.workflow.length ? row.workflow : ["M"],
      ui_priority: row.priority,
      status: row.status,
    };
  }),
});

export const workflowStatsFromSummary = <T extends { label: string; value: string; helper: string }>(
  summary: ApiRecord,
  fallback: T[],
): T[] => {
  const read = (keys: string[], defaultValue: string) => {
    for (const key of keys) {
      const value = summary[key];
      if (typeof value === "number") return String(value);
      if (typeof value === "string" && value.trim()) return value;
    }
    return defaultValue;
  };

  return fallback.map((stat) => {
    if (stat.label === "Active Workflows") {
      return {
        ...stat,
        value: read(["active_workflows", "activeWorkflows"], stat.value),
        helper: "Live from approval policy",
      };
    }
    if (stat.label === "Transactions Covered") {
      return {
        ...stat,
        value: read(["transaction_types_covered", "transactionTypesCovered"], stat.value),
        helper: "Distinct transaction scopes",
      };
    }
    if (stat.label === "Total Rule Sets") {
      return {
        ...stat,
        value: read(["total_rule_sets", "totalRuleSets", "total_workflows"], stat.value),
        helper: "Workflow bands in policy",
      };
    }
    if (stat.label === "Users in Workflow") {
      return {
        ...stat,
        value: read(["users_in_workflow", "usersInWorkflow"], stat.value),
        helper: "Assigned approvers/checkers",
      };
    }
    return stat;
  });
};
